import React, { useEffect, useState } from 'react';
import { jobsApi, getErrorMessage } from '../../services/api';
import { formatINR } from '../command-center/constants';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Loader2, Factory, AlertTriangle, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import QuickFixIndustryDrawer from './QuickFixIndustryDrawer';

const PERIODS = [
  { value: 'current_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'all_time', label: 'All-time' },
];

/**
 * Reports → "Joined CTC by Industry".
 *
 * Aggregates joined-stage applications by the client's industry. Surfaces
 * which industries are driving the most placement revenue, and flags the
 * data-hygiene gap when many closures land in the "Not specified" bucket.
 *
 * RBAC: backend gates this to internal revenue roles. Component returns
 * null on 403 so client_spoc and similar external roles never see the card.
 */
const JoinedByIndustryCard = () => {
  const [period, setPeriod] = useState('all_time');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [quickFixOpen, setQuickFixOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    jobsApi.joinedByIndustry(period)
      .then((res) => { if (!cancelled) setData(res.data || null); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
          return;
        }
        toast.error(getErrorMessage(err, 'Could not load industry totals'));
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, reloadKey]);

  // Refresh as soon as the user backfills an industry inside the drawer.
  useEffect(() => {
    const onBackfill = () => setReloadKey((k) => k + 1);
    window.addEventListener('industry:backfilled', onBackfill);
    return () => window.removeEventListener('industry:backfilled', onBackfill);
  }, []);

  if (forbidden) return null;

  const industries = data?.industries || [];
  const grandCtc = data?.grand_ctc || 0;
  const grandCount = data?.grand_count || 0;
  // Use the biggest bucket as the visual scale anchor so the longest bar
  // always pegs ~100% width.
  const maxCtc = industries.reduce((m, it) => Math.max(m, it.joined_ctc || 0), 0);

  // Detect a meaningful data-hygiene gap: when "Not specified" eats more
  // than 25% of the total CTC, surface a soft warning above the chart so
  // admins know to backfill `clients.industry` from Settings.
  const notSpecified = industries.find((it) => it.industry === 'Not specified');
  const notSpecifiedShare = notSpecified ? Number(notSpecified.share_pct || 0) : 0;
  const hygieneWarning = notSpecifiedShare >= 25;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white"
      data-testid="joined-by-industry-card"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Factory className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-slate-800">Joined CTC by Industry</h3>
          {data ? (
            <span className="text-xs text-slate-500 truncate">
              · {industries.length} {industries.length === 1 ? 'industry' : 'industries'} · {grandCount} {grandCount === 1 ? 'closure' : 'closures'} · total ₹{formatINR(grandCtc) || '0'}
            </span>
          ) : null}
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="joined-by-industry-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value} className="text-xs">{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading industry split…
          </div>
        ) : industries.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-8" data-testid="joined-by-industry-empty">
            No closures in this period yet.
          </div>
        ) : (
          <>
            {hygieneWarning ? (
              <div
                className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[12px] text-amber-900"
                data-testid="joined-by-industry-hygiene"
              >
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
                <div className="flex-1 min-w-0">
                  <strong>{notSpecifiedShare.toFixed(1)}%</strong> of joined CTC sits under <em>Not specified</em>.
                  Backfill <strong>industry</strong> on those client profiles to see a complete revenue split.
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setQuickFixOpen(true)}
                  className="h-7 border-amber-300 text-amber-900 hover:bg-amber-100"
                  data-testid="joined-by-industry-quickfix-btn"
                >
                  <Wand2 className="w-3 h-3 mr-1" /> Quick-fix
                </Button>
              </div>
            ) : null}

            <ul className="space-y-2.5">
              {industries.map((row, idx) => {
                const width = maxCtc > 0 ? Math.max(2, Math.round((row.joined_ctc / maxCtc) * 100)) : 0;
                const isMuted = row.industry === 'Not specified';
                return (
                  <li
                    key={row.industry}
                    data-testid={`joined-by-industry-row-${idx}`}
                    className="group"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-1">
                      <span
                        className={`text-sm font-medium truncate ${isMuted ? 'text-slate-500 italic' : 'text-slate-800'}`}
                        title={row.industry}
                      >
                        {row.industry}
                      </span>
                      <span className="flex items-baseline gap-2 text-xs whitespace-nowrap">
                        <span className="text-slate-500">
                          {row.joined_count} {row.joined_count === 1 ? 'closure' : 'closures'}
                        </span>
                        <span className="font-mono font-semibold text-emerald-700">
                          ₹{formatINR(row.joined_ctc) || '0'}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                          {row.share_pct}%
                        </span>
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${isMuted ? 'bg-slate-300' : 'bg-gradient-to-r from-teal-400 to-emerald-500'}`}
                        style={{ width: `${width}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
      <QuickFixIndustryDrawer open={quickFixOpen} onOpenChange={setQuickFixOpen} />
    </div>
  );
};

export default JoinedByIndustryCard;
